import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, FileText, ShieldCheck, TrendingUp } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import InvestorDashboard from "@/components/investor/InvestorDashboard";
import api from "@/lib/api";
import useSeo from "@/hooks/useSeo";

/**
 * Portal — prospect + investor private area.
 * Prospects see docs/project; investors get the real-time RubiPOS dashboard.
 */
export default function Portal() {
  useSeo({ title: "Portal del inversionista", description: "Portal privado: contratos, presentaciones y avance de tu proyecto.", path: "/portal", noindex: true });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/portal/me");
        if (data.user.must_change_password) { navigate("/portal/password", { replace: true }); return; }
        setData(data);
      } catch (_e) {
        navigate("/login?portal=1");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_e) {
      /* ignore */
    }
    // Recarga dura: resetea todo el estado de auth en memoria y evita el
    // bucle /login <-> /portal cuando la sesión ya se limpió en el servidor.
    window.location.href = "/login?portal=1";
  };

  if (loading) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center font-mono text-xs uppercase tracking-widest text-[#52525B]">Cargando portal…</div>;
  if (!data) return null;

  const { user, project } = data;
  const isInvestor = user.role === "inversionista";
  if (isInvestor) return <InvestorDashboard user={user} documents={data.documents} onLogout={logout} />;

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#0A0A0A]">
      {/* Top bar */}
      <header className="border-b border-black/6 bg-white/80 backdrop-blur sticky top-0 z-40">
        <div className="max-w-[1200px] mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <BrandLogo size={30} />
            <div>
              <div className="font-display text-sm">Expansión Rubí</div>
              <div className="font-mono text-[9px] uppercase tracking-widest text-[#FFBF57]">Portal · {isInvestor ? "Inversionista" : "Interesado"}</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <div className="text-right hidden md:block">
              <div className="text-xs font-medium">{user.name}</div>
              <div className="text-[10px] text-[#71717A]">{user.email}</div>
            </div>
            <button onClick={logout} className="text-xs uppercase tracking-widest font-mono text-[#52525B] hover:text-black flex items-center gap-2 border border-black/10 px-3 py-2 rounded-full" data-testid="portal-logout">
              <LogOut size={12} /> Salir
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-6 py-10 md:py-14">
        <div className="mb-10">
          <div className="text-[11px] uppercase tracking-widest text-[#FFBF57] font-mono">Bienvenido</div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl leading-tight">{user.name.split(" ")[0]}</h1>
          <p className="mt-3 text-[#3F3F46] max-w-xl">
            {isInvestor
              ? "Acceso al seguimiento de tu participación, documentación, contrato y desempeño en tiempo real."
              : "Este es tu espacio privado. Aquí encontrarás la documentación, el contrato modelo y el avance del proyecto de interés."}
          </p>
        </div>

        {/* Assigned project card */}
        {project ? (
          <div className="card-light p-8 mb-8" data-testid="portal-project-card">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] uppercase tracking-widest text-[#71717A] font-mono">Proyecto asignado</div>
              <span className="stage-badge stage-review"><span className="dot" />En evaluación privada</span>
            </div>
            <div className="font-display text-3xl mb-2">{project.code}</div>
            <div className="text-sm text-[#52525B] mb-6">{project.city}</div>
            {project.note && <p className="text-sm text-[#3F3F46] leading-relaxed">{project.note}</p>}
            <Link to={`/proyecto/${encodeURIComponent(project.code)}`} className="btn-outline mt-6 !text-xs !py-2.5 !px-4" data-testid="portal-project-link">
              Ver detalle del proyecto
            </Link>
          </div>
        ) : (
          <div className="card-light p-8 mb-8 text-center border-dashed">
            <div className="text-sm text-[#52525B]">Aún no se te ha asignado un proyecto específico. Tu asesor comercial lo hará en la siguiente etapa.</div>
          </div>
        )}

        {/* Documents grid — real assigned docs from CMS + placeholders when empty */}
        <h2 className="text-[11px] uppercase tracking-widest text-[#52525B] font-mono mb-4">Documentación disponible</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
          {data.documents && data.documents.length > 0 ? (
            data.documents.map((doc) => (
              <a
                key={doc.doc_id}
                href={doc.url.startsWith("http") ? doc.url : `${process.env.REACT_APP_BACKEND_URL}${doc.url}`}
                target="_blank"
                rel="noreferrer"
                className="card-light p-5 h-full block hover:border-[#FFBF57] transition-colors group"
                data-testid={`portal-doc-${doc.doc_id}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-[#FFBF57]/12 border border-[#FFBF57]/30 flex items-center justify-center text-[#0A0A0A]">
                    <FileText size={18} />
                  </div>
                  <span className="text-[9px] uppercase tracking-widest font-mono text-[#FFBF57]">{doc.category || "Documento"}</span>
                </div>
                <div className="font-display text-base text-[#0A0A0A] group-hover:underline">{doc.title}</div>
                {doc.note && <p className="text-xs text-[#52525B] mt-1 leading-relaxed">{doc.note}</p>}
                <div className="text-[10px] text-[#71717A] mt-3 font-mono">Descargar / abrir →</div>
              </a>
            ))
          ) : (
            <>
              <DocCard icon={<FileText size={18} />} title="Presentación ejecutiva" body="Tu asesor comercial la asignará próximamente." status="disponible-pronto" />
              <DocCard icon={<ShieldCheck size={18} />} title="Contrato AEP" body="Se compartirá al iniciar la etapa de documentación." status="disponible-pronto" />
              <DocCard icon={<TrendingUp size={18} />} title="Modelo financiero" body="Escenarios y proyecciones específicos del proyecto." status="disponible-pronto" />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function DocCard({ icon, title, body, status }) {
  const isReady = status === "disponible";
  return (
    <div className={`card-light p-5 h-full ${isReady ? "" : "opacity-70"}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-lg bg-[#FFBF57]/12 border border-[#FFBF57]/30 flex items-center justify-center text-[#0A0A0A]">{icon}</div>
        <span className="text-[9px] uppercase tracking-widest font-mono text-[#71717A]">{isReady ? "Disponible" : "Disponible pronto"}</span>
      </div>
      <div className="font-display text-base text-[#0A0A0A]">{title}</div>
      <p className="text-xs text-[#52525B] mt-1 leading-relaxed">{body}</p>
    </div>
  );
}
